import { useCallback, useEffect, useRef, useState } from 'react';
import {
  NamiCampaignManager,
  NamiCustomerManager,
  NamiFlowManager,
  NamiPaywallAction,
  NamiPaywallEvent,
  NamiPaywallLaunchContext,
  NamiPaywallManager,
} from '@namiml/web-sdk';
import ErrorMessage from '../components/ErrorMessage';
import LaunchContext from '../components/LaunchContext';
import { productDetails } from '../data/product-details';
import {
  NAMI_PAYWALL_CONTEXT,
  NAMI_SHOW_PAYWALL,
} from '../utils/const';
import { useNavigation } from '../navigation/NavigationProvider';
import { logPaywallAction } from '../utils/logPaywallAction';
import { flowTranscript } from '../test/FlowTranscript';

const APP_SUPPLIED_VIDEO_URL =
  'https://storage.googleapis.com/gtv-videos-bucket/sample/BigBuckBunny.mp4';
const APP_SUPPLIED_VIDEO_NAME = 'app-supplied-video';

const PaywallPage = () => {
  const navigation = useNavigation();
  const label = navigation.query.get('label');
  const url = navigation.query.get('url');
  const requestedShow = navigation.query.get('showPaywall') === 'true';

  const paywallContainerRef = useRef<HTMLDivElement | null>(null);
  const [error, setError] = useState('');
  const [showPaywall, setShowPaywall] = useState(requestedShow);
  const [context, setContext] = useState<NamiPaywallLaunchContext | undefined>();
  const [videoDelay, setVideoDelay] = useState<number>(0);
  // The paywall itself renders inside the SDK web components' shadow DOM, which
  // UI drivers (Maestro web) cannot inspect. Track the active page from paywall
  // action events and mirror it as a light-DOM marker so E2E flows can gate on
  // "screen X is showing" deterministically (see e2e/ctv-flow/).
  const [activePaywallId, setActivePaywallId] = useState<string | null>(null);

  const saveShowPaywall = useCallback((value: boolean) => {
    setShowPaywall(value);
    localStorage.setItem(NAMI_SHOW_PAYWALL, String(value));
  }, []);

  useEffect(() => {
    setShowPaywall(requestedShow || localStorage.getItem(NAMI_SHOW_PAYWALL) === 'true');
  }, [requestedShow]);

  const launchPaywallWithContext = useCallback(
    (launchContext: NamiPaywallLaunchContext, appVideoDelay: number) => {
      saveShowPaywall(true);
      setContext(launchContext);
      setVideoDelay(appVideoDelay);
    },
    [saveShowPaywall]
  );

  const triggerLaunch = useCallback(() => {
    if (!showPaywall) return;
    try {
      if (videoDelay > 0) {
        setTimeout(() => {
          NamiPaywallManager.setAppSuppliedVideoDetails(
            APP_SUPPLIED_VIDEO_URL,
            APP_SUPPLIED_VIDEO_NAME
          );
        }, videoDelay * 1000);
      }

      const resolvedContext =
        context ??
        (() => {
          const stored = localStorage.getItem(NAMI_PAYWALL_CONTEXT);
          return stored ? (JSON.parse(stored) as NamiPaywallLaunchContext) : undefined;
        })();

      const resultHandler = (success: boolean, err?: unknown) => {
        if (!success) {
          if (err) {
            console.error('Launch result handler error', err);
            setError(`Launch failed: ${String(err)}`);
          }
          localStorage.removeItem(NAMI_SHOW_PAYWALL);
          navigation.navigate('/');
        }
      };

      const actionHandler = (event: NamiPaywallEvent) => {
        logPaywallAction(event);
        if (event.action === NamiPaywallAction.SHOW_PAYWALL) {
          flowTranscript.record('paywall', {
            action: 'SHOW_PAYWALL',
            paywallId: event.paywallId ?? '',
          });
        }
        if (
          event.paywallId &&
          (event.action === NamiPaywallAction.SHOW_PAYWALL ||
            event.action === NamiPaywallAction.PAGE_CHANGE)
        ) {
          setActivePaywallId(event.paywallId);
        }
        if (event.action === NamiPaywallAction.CLOSE_PAYWALL) {
          setActivePaywallId(null);
        }
      };

      const paywallComponent = NamiCampaignManager.launch(
        label ?? undefined,
        url ?? undefined,
        resolvedContext,
        resultHandler,
        actionHandler
      );

      NamiFlowManager.registerEventHandler((event: unknown) => {
        console.log('Flow event handler data', event);
        flowTranscript.record('event', event);
      });

      NamiFlowManager.registerStepHandoff((handoffTag, handoffData, complete) => {
        console.log('Step handoff', handoffTag, handoffData);
        flowTranscript.record('handoff', { handoffTag, handoffData });
        if (handoffTag === 'signin') {
          // Login outcome: signs the user in AND resumes the flow in one
          // call; the open-ended attributes let flows branch on the host's
          // own keys (e.g. login.subscribedTier — premium → skip the upsell).
          // NAM-5123: the NFL flow's paywall pages leave through __resume__ ->
          // flowExit, gated on UserTags.currentSubscriber, which the tvOS and
          // Android test apps set on login.
          NamiCustomerManager.setCustomerAttribute('currentSubscriber', 'true');
          complete({
            kind: 'login',
            result: 'success',
            details: { loginId: '777888999', attributes: { subscribedTier: 'premium' } },
          });
          return;
        }
        if (handoffTag === 'complete') {
          // Clear the login attribute so it cannot leak into the next e2e case:
          // "Check for Recent Session" exits at once when currentSubscriber is
          // true, and the web suite does not clear browser state between cases.
          NamiCustomerManager.clearCustomerAttribute('currentSubscriber');
          complete();
          navigation.navigate('/');
          return;
        }
        setTimeout(() => complete(), 1000);
      });

      NamiPaywallManager.registerBuySkuHandler((sku) => {
        console.log('Buy SKU Handler triggered with handoff data:', {
          skuId: sku.id,
          skuName: sku.name,
          productId: sku.skuId,
          type: sku.type,
          promoId: sku.promoId,
          promoToken: sku.promoToken,
          productDetails: sku.productDetails,
          appleProduct: sku.appleProduct,
          googleProduct: sku.googleProduct,
          amazonProduct: sku.amazonProduct
        });
        console.log('Full SKU object:', sku);
      });

      const unregisterCloseHandler = NamiPaywallManager.registerCloseHandler(() => {
        unregisterCloseHandler();
        saveShowPaywall(false);
        navigation.navigate('/');
      });

      if (paywallComponent && paywallContainerRef.current) {
        paywallContainerRef.current.innerHTML = '';
        paywallContainerRef.current.appendChild(paywallComponent as Node);
      }
    } catch (err) {
      const details = err instanceof Error ? err.message : String(err);
      setError(`Error rendering paywall: ${details}`);
      saveShowPaywall(false);
    }
  }, [context, label, navigation, saveShowPaywall, showPaywall, url, videoDelay]);

  useEffect(() => {
    NamiPaywallManager.setProductDetails(productDetails);
  }, []);

  useEffect(() => {
    triggerLaunch();
  }, [triggerLaunch]);

  return (
    <>
      {error && <ErrorMessage message={error} />}
      {/* Light-DOM marker for the shadow-DOM paywall page currently showing.
          2×2px and near-invisible so screenshot baselines are unaffected, but
          present in the a11y/DOM tree for Maestro `id:` gates. */}
      {showPaywall && activePaywallId && (
        <div
          id={`paywall-active-${activePaywallId}`}
          data-testid={`paywall-active-${activePaywallId}`}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            width: 2,
            height: 2,
            overflow: 'hidden',
            zIndex: 2147483647,
            fontSize: 2,
            lineHeight: '2px',
            color: 'rgba(255,255,255,0.02)',
          }}
        >
          {activePaywallId}
        </div>
      )}
      {showPaywall ? (
        <div ref={paywallContainerRef} />
      ) : (
        <LaunchContext
          label={label}
          url={url}
          launchHandler={launchPaywallWithContext}
        />
      )}
    </>
  );
};

export default PaywallPage;
